import React, { useEffect, useState } from "react"
import { ENG_DIAG_QUESTION_COPY as QCOPY } from "../constants/eng-diag-question.constants"
import type {
  EngDiagQuestion,
  EngDiagStudentAnswers,
} from "../types/eng-diag.types"
import styles from "./eng-diag-question-view.module.css"
import theme from "./eng-diag-theme.module.css"
import { EngDiagRichContent } from "./EngDiagRichContent"

/*
 * A DELIBERATE COPY of the student app's EngDiagQuestionView
 * (student-frontend/src/modules/eng-diag/components/EngDiagQuestionView.tsx), so that a tutor
 * previewing a question sees what a child will see. The two differ only in how the instruction
 * and the content are rendered - see the note at the bottom of this file - and in where the
 * copy constants live. `diff` them when either changes.
 *
 * eng-diag-question-view.module.css and eng-diag-theme.module.css are byte-identical copies of
 * the student app's, kept that way so that diff stays useful. That leaves three dead rules
 * here - the audio button and the two cloze-gap classes - which the student app uses from
 * EngDiagRichContent and this app has no equivalent of.
 */

type Props = {
  question: EngDiagQuestion
  value?: EngDiagStudentAnswers
  onChange?: (answers: EngDiagStudentAnswers) => void
  /** When true, interactions still work for tutor try-out preview */
  readOnly?: boolean
}

function setAnswer(
  current: EngDiagStudentAnswers,
  key: string,
  next: string | null,
): EngDiagStudentAnswers {
  const copy = { ...current }
  if (next == null || next === "") delete copy[key]
  else copy[key] = next
  return copy
}

/** A, B, C… for a choice list. Falls back to the number past Z, which no question reaches. */
function optionLetter(index: number): string {
  return index < 26 ? String.fromCharCode(65 + index) : String(index + 1)
}

/**
 * The punctuation marks a `punctuation_insert` question offers, for the chip row.
 *
 * The union of the authored bank and every mark the sites will accept, because the two
 * disagree in the live bank and each carries something the other does not: the BANK may hold
 * deliberate distractors, while the SITES are the only authoritative statement of what
 * actually fits. Three live questions were unanswerable on the bank alone. Kept identical to
 * the student app - a tutor previewing a question has to see the marks the child will see.
 */
function punctuationBank(options: any): string[] {
  const authored: string[] = options?.bank || []
  const fromSites: string[] = (options?.sites || []).flatMap(
    (site: { marks?: string[] }) => site?.marks || [],
  )
  const marks = [...new Set([...authored, ...fromSites])]
  // A question with neither is broken, but there should still be something to tap.
  return marks.length ? marks : [",", ".", "?", "!"]
}

/**
 * How big a free-text answer box should be, taken from the longest answer the AUTHOR wrote.
 *
 * Every box used to be a five-row textarea, so a dictation question asking for the word "sign"
 * gave the same acre of paper as a piece of writing. Sizing off the strand is wrong:
 * `open_ended` is used in Spelling, Grammar, Reading and Vocabulary, and the answers inside
 * one strand run from a single word to a full sentence. An answer nobody authored means a
 * manually-marked question - the writing ones - which is where the big box belongs.
 *
 * Kept identical to the student app: this preview exists so a tutor sees what the child sees.
 */
const ANSWER_BOX = {
  /** A word or two. One line, like a `blank` row. */
  WORD_MAX_CHARS: 16,
  /** A sentence. Two lines, not a page. */
  SENTENCE_MAX_CHARS: 120,
}

type AnswerBoxSize = "word" | "sentence" | "writing"

function answerBoxSize(field: any): AnswerBoxSize {
  const lengths = (field?.answer || []).map((entry: any) => String(entry?.value ?? "").length)
  const longest = lengths.length ? Math.max(...lengths) : 0
  if (longest === 0) return "writing"
  if (longest <= ANSWER_BOX.WORD_MAX_CHARS) return "word"
  if (longest <= ANSWER_BOX.SENTENCE_MAX_CHARS) return "sentence"
  return "writing"
}


export function EngDiagQuestionView({
  question,
  value,
  onChange,
  readOnly = false,
}: Props) {
  void readOnly
  const [local, setLocal] = useState<EngDiagStudentAnswers>(value || {})
  const [selectedBankId, setSelectedBankId] = useState<string | null>(null)
  const [selectedMark, setSelectedMark] = useState<string | null>(null)

  // Keyed on the question, not on `value`. The parent hands down a fresh answers object on
  // every keystroke or tap, so depending on it re-ran this after each interaction and wiped the
  // selection: for punctuation_insert - whose own hint reads "Tap a mark, then tap an insertion
  // point" - the student had to re-pick the mark for every site in the sentence.
  //
  // question.id is enough here. The student app keys on the session question instead, because
  // the same question can appear twice in one test (once in the base set, once down a
  // prerequisite chain); a preview has no session and walks a plain list.
  const questionKey = question.id

  useEffect(() => {
    setLocal(value || {})
    setSelectedBankId(null)
    setSelectedMark(null)
    // `value` is deliberately not a dependency; see above. When the parent controls the
    // answers, `local` is unused anyway - `answers` below prefers `value`.
  }, [questionKey])

  const answers = value ?? local

  const emit = (next: EngDiagStudentAnswers) => {
    if (!value) setLocal(next)
    onChange?.(next)
  }

  const disabled = false

  const placeBankItem = (targetId: string) => {
    if (disabled || !selectedBankId) return
    emit(setAnswer(answers, targetId, selectedBankId))
    setSelectedBankId(null)
  }

  const onDragStart = (itemId: string) => (event: React.DragEvent) => {
    event.dataTransfer.setData("text/plain", itemId)
    setSelectedBankId(itemId)
  }

  const onDropTarget = (targetId: string) => (event: React.DragEvent) => {
    event.preventDefault()
    const itemId = event.dataTransfer.getData("text/plain") || selectedBankId
    if (!itemId || disabled) return
    emit(setAnswer(answers, targetId, itemId))
    setSelectedBankId(null)
  }

  const toggleBankItem = (itemId: string) =>
    setSelectedBankId(prev => (prev === itemId ? null : itemId))

  const renderBank = (bank: Array<{ id: string; text: string }>) => (
    <div className={styles.engDiagSection}>
      <span className={styles.engDiagSectionLabel}>{QCOPY.WORD_BANK}</span>
      <div className={styles.engDiagPanel}>
        {bank.map(item => (
          <button
            key={item.id}
            type="button"
            // Placing a chip does NOT use it up: one bank word can be the answer to several
            // targets (the /ir/ in "bird", "girl" and "shirt" is one question, not three), so
            // unlike the sort and the rebuild, nothing here is ever removed or ghosted.
            draggable={!disabled}
            onDragStart={onDragStart(item.id)}
            className={`${styles.engDiagBankItem} ${
              selectedBankId === item.id ? styles.engDiagBankItemActive : ""
            }`}
            aria-pressed={selectedBankId === item.id}
            disabled={disabled}
            onClick={() => toggleBankItem(item.id)}
          >
            {item.text}
          </button>
        ))}
      </div>
    </div>
  )

  /**
   * A question with nothing for the student to answer: they read aloud, or just look at
   * something, and a tutor marks it from the lesson report afterwards.
   *
   * Before this, all four of those types fell through to the default branch and a child was
   * shown "Unsupported type: read_words".
   */
  const renderNoAnswerNote = (icon: string, title: string, body: string) => (
    <div className={styles.engDiagNoAnswerNote}>
      <span className={styles.engDiagNoAnswerIcon} aria-hidden="true">
        <i className={icon} />
      </span>
      <div>
        <div className={styles.engDiagNoAnswerTitle}>{title}</div>
        <div className={styles.engDiagNoAnswerBody}>{body}</div>
      </div>
    </div>
  )

  /** The words of a read-aloud list, laid out the number-per-row the author chose. */
  const renderWordList = () => {
    const words = question.options?.words || []
    const perRow = Math.max(1, Number(question.options?.wordsPerRow) || 3)
    return (
      <div
        className={styles.engDiagWordList}
        style={{ gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` }}
      >
        {words.map((word: any) => (
          <div key={word.id} className={styles.engDiagWordListItem}>
            {word.text}
          </div>
        ))}
      </div>
    )
  }

  /** One multiple-choice list. Shared by `mcq` and by an mcq comprehension sub-question. */
  const renderChoices = (
    choices: Array<{ id: string; content: string }>,
    answerKey: string,
    label: string,
  ) => (
    <div className={styles.engDiagChoices} role="radiogroup" aria-label={label}>
      {choices.map((choice, index) => {
        const on = answers[answerKey] === choice.id
        const choose = () => emit(setAnswer(answers, answerKey, choice.id))
        return (
          // A div rather than a button, because an option's content can itself contain a
          // control: a listening question puts the clip IN the option, and the play button
          // for it cannot live inside a <button>. role/tabIndex/keydown keep it behaving and
          // reading as the radio it was.
          <div
            key={choice.id}
            role="radio"
            tabIndex={0}
            aria-checked={on}
            className={`${styles.engDiagChoice} ${on ? styles.engDiagChoiceActive : ""}`}
            onClick={choose}
            onKeyDown={event => {
              if (event.key === " " || event.key === "Enter") {
                event.preventDefault()
                choose()
              }
            }}
          >
            <span
              className={`${styles.engDiagChoiceBadge} ${
                on ? styles.engDiagChoiceBadgeActive : ""
              }`}
              aria-hidden="true"
            >
              {optionLetter(index)}
            </span>
            {/* Through the rich renderer, not raw HTML: LCMS stores a clip in an option as an
                empty placeholder span, so the Reception letter-sound questions previewed as
                four blank options. */}
            <EngDiagRichContent
              className={`${styles.engDiagChoiceText} ${
                on ? styles.engDiagChoiceTextActive : ""
              }`}
              html={choice.content}
            />
            {/* Only on the chosen option, so it reads as a confirmation rather than as a
                checkbox the student has to go and find. */}
            {on ? (
              <span className={styles.engDiagChoiceTick} aria-hidden="true">
                <i className="fas fa-check" />
              </span>
            ) : null}
          </div>
        )
      })}
    </div>
  )

  const renderBody = () => {
    switch (question.type) {
      case "mcq":
        return renderChoices(
          question.options?.choices || [],
          question.id,
          "Answer options",
        )

      case "blank": {
        const blanks = question.options?.blanks || []
        // One blank needs no numbering - the sentence above already shows where it goes. Several
        // do, because the inputs sit in a column away from the gaps they fill.
        if (blanks.length === 1) {
          const blank = blanks[0]
          const typed = answers[blank.id] || ""
          return (
            <div className={styles.engDiagField}>
              <label className={styles.engDiagFieldLabel} htmlFor={`ed-${blank.id}`}>
                {QCOPY.TYPE_YOUR_ANSWER}
              </label>
              <input
                id={`ed-${blank.id}`}
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder={QCOPY.TYPE_HERE}
                className={`${styles.engDiagInput} ${
                  typed.trim() ? styles.engDiagInputFilled : ""
                }`}
                value={typed}
                onChange={e => emit(setAnswer(answers, blank.id, e.target.value))}
              />
            </div>
          )
        }
        return (
          <div className={styles.engDiagRows}>
            {blanks.map((blank: any, index: number) => {
              const typed = answers[blank.id] || ""
              return (
                <div key={blank.id} className={styles.engDiagRow}>
                  <span className={styles.engDiagRowIndex} aria-hidden="true">
                    {index + 1}
                  </span>
                  <label className={styles.engDiagFieldLabel} htmlFor={`ed-${blank.id}`}>
                    {QCOPY.BLANK_LABEL} {index + 1}
                  </label>
                  <input
                    id={`ed-${blank.id}`}
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={QCOPY.TYPE_YOUR_ANSWER}
                    className={`${styles.engDiagInput} ${
                      typed.trim() ? styles.engDiagInputFilled : ""
                    }`}
                    style={{ flex: "1 1 240px", width: "auto" }}
                    value={typed}
                    onChange={e => emit(setAnswer(answers, blank.id, e.target.value))}
                  />
                </div>
              )
            })}
          </div>
        )
      }
      case "open_ended": {
        // Authors set the order; without this the fields came out in whatever order the stored
        // JSON happened to hold, which for a two-part question is the wrong two parts.
        const fields = [...(question.options?.fields || [])].sort(
          (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0),
        )
        const renderControl = (field: any, inRow: boolean) => {
          const typed = answers[field.id] || ""
          const size = answerBoxSize(field)
          const filled = typed.trim() ? styles.engDiagInputFilled : ""
          const onChange = (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => emit(setAnswer(answers, field.id, event.target.value))
          if (size === "word") {
            return (
              <input
                id={`ed-${field.id}`}
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder={QCOPY.TYPE_HERE}
                className={`${styles.engDiagInput} ${filled}`}
                // Same as a `blank` row: the box shares the line with its number and label
                // rather than taking the full width and pushing them onto their own.
                style={inRow ? { flex: "1 1 240px", width: "auto" } : undefined}
                value={typed}
                onChange={onChange}
              />
            )
          }
          return (
            <textarea
              id={`ed-${field.id}`}
              spellCheck={false}
              placeholder={QCOPY.WRITE_HERE}
              className={`${styles.engDiagTextarea} ${
                size === "sentence" ? styles.engDiagTextareaShort : ""
              } ${filled}`}
              style={inRow ? { flex: "1 1 280px", width: "auto" } : undefined}
              value={typed}
              onChange={onChange}
            />
          )
        }
        // One field needs no number - the question above it says what to write. Several do,
        // and they get the numbered rows a multi-blank question already uses, because the
        // boxes sit in a column away from the thing they answer. This is what makes the five
        // dictated words readable as 1..5.
        if (fields.length === 1) {
          const field = fields[0]
          return (
            <div className={styles.engDiagField}>
              <label className={styles.engDiagFieldLabel} htmlFor={`ed-${field.id}`}>
                {field.label || QCOPY.YOUR_ANSWER}
              </label>
              {renderControl(field, false)}
            </div>
          )
        }
        return (
          <div className={styles.engDiagRows}>
            {fields.map((field: any, index: number) => (
              <div key={field.id} className={styles.engDiagRow}>
                <span className={styles.engDiagRowIndex} aria-hidden="true">
                  {index + 1}
                </span>
                <label className={styles.engDiagFieldLabel} htmlFor={`ed-${field.id}`}>
                  {field.label || `${QCOPY.YOUR_ANSWER} ${index + 1}`}
                </label>
                {renderControl(field, true)}
              </div>
            ))}
          </div>
        )
      }
      case "mc_cloze": {
        const blanks = question.options?.blanks || []
        return (
          <div className={styles.engDiagRows}>
            {blanks.map((blank: any, index: number) => (
              <div key={blank.id} className={styles.engDiagRow}>
                <span className={styles.engDiagRowIndex} aria-hidden="true">
                  {index + 1}
                </span>
                <span className={styles.engDiagRowPrompt}>
                  {QCOPY.GAP_LABEL} {index + 1}
                </span>
                <div
                  className={styles.engDiagBankRow}
                  role="radiogroup"
                  aria-label={`${QCOPY.GAP_LABEL} ${index + 1}`}
                >
                  {(blank.choices || []).map((choice: any) => {
                    const on = answers[blank.id] === choice.id
                    return (
                      <button
                        key={choice.id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        className={`${styles.engDiagChip} ${
                          on ? styles.engDiagChipActive : ""
                        }`}
                        onClick={() => emit(setAnswer(answers, blank.id, choice.id))}
                      >
                        {choice.content}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      }
      case "spelling_drop":
      case "punctuation_drop":
      // A column match is the same interaction with the left column standing in for the blanks,
      // so it maps its pairs onto this player rather than repeating it.
      case "column_match": {
        const blanks =
          question.type === "column_match"
            ? (question.options?.pairs || []).map((pair: any) => ({
                id: pair.id,
                label: pair.leftText || pair.id,
                imageUrl: pair.leftImageUrl,
              }))
            : question.options?.blanks || []
        const bank = question.options?.bank || []
        const armed = selectedBankId !== null
        return (
          <>
            <p className={styles.engDiagHint}>{QCOPY.DROP_HINT}</p>
            {renderBank(bank)}
            <div className={styles.engDiagTargets}>
              {blanks.map((blank: any, index: number) => {
                const placedId = answers[blank.id]
                const placed = bank.find((b: any) => b.id === placedId)
                return (
                  <div
                    key={blank.id}
                    className={`${styles.engDiagDropTarget} ${
                      placed ? styles.engDiagDropTargetFilled : ""
                    }`}
                    onDragOver={e => e.preventDefault()}
                    onDrop={onDropTarget(blank.id)}
                    onClick={() => placeBankItem(blank.id)}
                  >
                    <span className={styles.engDiagRowIndex} aria-hidden="true">
                      {index + 1}
                    </span>
                    {/* A spelling drop prompts with a picture, and a column match may do the
                        same on the left. Without this the target showed only its label, which
                        for a picture-prompted blank is nothing at all. */}
                    {blank.imageUrl && (
                      <img
                        src={blank.imageUrl}
                        alt={blank.label || ""}
                        className={styles.engDiagDropTargetImage}
                      />
                    )}
                    {blank.label && (
                      <span className={styles.engDiagDropTargetLabel}>{blank.label}</span>
                    )}
                    <span
                      className={`${styles.engDiagSlot} ${
                        placed
                          ? styles.engDiagSlotFilled
                          : armed
                            ? styles.engDiagSlotArmed
                            : ""
                      }`}
                    >
                      <span>
                        {placed
                          ? placed.text
                          : armed
                            ? QCOPY.DROP_ARMED
                            : QCOPY.DROP_EMPTY}
                      </span>
                      {/* Dropping a different word on a filled space already replaces what is
                          there, but nothing could EMPTY one: a child who placed a word by
                          mistake was stuck with it, and a space they meant to leave blank was
                          marked wrong. Its own button rather than a click on the slot, so that
                          replacing still takes one move. stopPropagation so clearing does not
                          immediately re-place the word that is still selected. */}
                      {placed ? (
                        <button
                          type="button"
                          className={styles.engDiagSlotClear}
                          aria-label={`Clear ${placed.text}`}
                          title="Clear"
                          onClick={event => {
                            event.stopPropagation()
                            emit(setAnswer(answers, blank.id, null))
                          }}
                        >
                          <i className="fas fa-times" aria-hidden="true" />
                        </button>
                      ) : null}
                    </span>
                  </div>
                )
              })}
            </div>
          </>
        )
      }
      case "image_sort": {
        const categories = question.options?.categories || []
        const items = question.options?.items || []
        /*
         * A sorted card LEAVES the pool rather than dimming inside it. Dimmed-but-still-there
         * read as spent, so a child who wanted to move a card had no reason to try the pool
         * again - and the gesture they did try, dragging it back out of the group, did nothing,
         * because a card in a group was a label and not a control. The pool is now what is left
         * to sort, and a card in a group is what moves it or takes it back.
         */
        const unsorted = items.filter((item: any) => !answers[item.id])
        const armed = selectedBankId !== null
        const place = (itemId: string, categoryId: string) => {
          emit(setAnswer(answers, itemId, categoryId))
          setSelectedBankId(null)
        }
        // The type is called image_sort and its items carry an imageUrl, which was never
        // rendered: a picture card came out as its database id.
        const cardFace = (item: any) =>
          item.imageUrl ? (
            <img
              src={item.imageUrl}
              alt={item.text || ""}
              className={styles.engDiagDropTargetImage}
            />
          ) : (
            <span>{item.text || item.id}</span>
          )
        return (
          <>
            <p className={styles.engDiagHint}>{QCOPY.SORT_HINT}</p>
            <div className={styles.engDiagSection}>
              <span className={styles.engDiagSectionLabel}>{QCOPY.SORT_POOL_LABEL}</span>
              <div className={styles.engDiagPanel}>
                {unsorted.map((item: any) => (
                  <button
                    key={item.id}
                    type="button"
                    draggable={!disabled}
                    onDragStart={onDragStart(item.id)}
                    className={`${styles.engDiagSortItem} ${
                      selectedBankId === item.id ? styles.engDiagBankItemActive : ""
                    }`}
                    aria-pressed={selectedBankId === item.id}
                    onClick={() => toggleBankItem(item.id)}
                  >
                    {cardFace(item)}
                  </button>
                ))}
                {!unsorted.length ? (
                  <span className={styles.engDiagHint}>{QCOPY.SORT_POOL_EMPTY}</span>
                ) : null}
              </div>
            </div>
            <div className={styles.engDiagBins}>
              {categories.map((cat: any) => {
                const inCat = items.filter((item: any) => answers[item.id] === cat.id)
                return (
                  <div key={cat.id} className={styles.engDiagBin}>
                    {/* The head is a drop control too, and it lights up while a card is
                        selected, so the child is shown where the card can go before they
                        tap. */}
                    <button
                      type="button"
                      className={`${styles.engDiagBinHead} ${
                        armed ? styles.engDiagBinHeadArmed : ""
                      }`}
                      onClick={() => {
                        if (!selectedBankId) return
                        place(selectedBankId, cat.id)
                      }}
                    >
                      <span>{cat.name}</span>
                      <span className={styles.engDiagBinCount}>{inCat.length}</span>
                    </button>
                    <div
                      className={`${styles.engDiagBinBody} ${
                        armed ? styles.engDiagBinBodyArmed : ""
                      }`}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => {
                        e.preventDefault()
                        const itemId =
                          e.dataTransfer.getData("text/plain") || selectedBankId
                        if (!itemId) return
                        place(itemId, cat.id)
                      }}
                      onClick={() => {
                        if (!selectedBankId) return
                        place(selectedBankId, cat.id)
                      }}
                    >
                      {inCat.map((item: any) => (
                        <button
                          key={item.id}
                          type="button"
                          /* Draggable so a card can go straight from one group to another, and
                             clickable so it can go back to the pool: the two ways a child
                             changes their mind. */
                          draggable={!disabled}
                          onDragStart={onDragStart(item.id)}
                          className={styles.engDiagSortedChip}
                          aria-label={`Take ${item.text || item.id} out of ${cat.name}`}
                          onClick={event => {
                            event.stopPropagation()
                            emit(setAnswer(answers, item.id, null))
                          }}
                        >
                          {cardFace(item)}
                          <span aria-hidden="true" className={styles.engDiagChipRemove}>
                            <i className="fas fa-times" />
                          </span>
                        </button>
                      ))}
                      {!inCat.length ? (
                        <span className={styles.engDiagBinEmpty}>{QCOPY.SORT_BIN_EMPTY}</span>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )
      }
      case "punctuation_insert": {
        const sentence = question.options?.sentence || ""
        const sites: Array<{ id: string; offset: number; marks?: string[] }> =
          question.options?.sites || []
        const bank = punctuationBank(question.options)
        const siteByOffset = new Map<
          number,
          { id: string; offset: number; marks?: string[] }
        >(sites.map(s => [Number(s.offset), s]))
        const chars = sentence.split("")
        const renderSite = (
          site?: { id: string; offset: number; marks?: string[] }
        ) =>
          site ? (
            <button
              key={`site-${site.id}`}
              type="button"
              className={`${styles.engDiagSiteBtn} ${
                answers[site.id] ? styles.engDiagSiteBtnFilled : ""
              }`}
              aria-label={
                answers[site.id]
                  ? `Change the mark here, currently ${answers[site.id]}`
                  : "Put a mark here"
              }
              onClick={() => {
                if (!selectedMark) return
                emit(setAnswer(answers, site.id, selectedMark))
              }}
            >
              {answers[site.id] || "‸"}
            </button>
          ) : null
        return (
          <>
            <div className={styles.engDiagSection}>
              <span className={styles.engDiagSectionLabel}>{QCOPY.MARKS_LABEL}</span>
              <div className={styles.engDiagMarkBank}>
                {bank.map(mark => (
                  <button
                    key={mark}
                    type="button"
                    className={`${styles.engDiagChip} ${styles.engDiagMark} ${
                      selectedMark === mark ? styles.engDiagChipActive : ""
                    }`}
                    aria-pressed={selectedMark === mark}
                    onClick={() => setSelectedMark(prev => (prev === mark ? null : mark))}
                  >
                    {mark}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.engDiagPassage}>
              {chars.map((ch: string, index: number) => (
                <React.Fragment key={`${index}-${ch}`}>
                  {renderSite(siteByOffset.get(index))}
                  <span>{ch}</span>
                </React.Fragment>
              ))}
              {/* A site at offset === sentence.length is the end-of-sentence position, used by a
                  trailing full stop or question mark. There is no character to render it before,
                  so it is emitted after the last one - without this the button is never drawn at
                  all and the question cannot be answered. */}
              {renderSite(siteByOffset.get(chars.length))}
            </div>
            <p className={styles.engDiagHint}>{QCOPY.INSERT_HINT}</p>
          </>
        )
      }
      case "text_select": {
        const mode = question.options?.mode || "word"
        const passage = question.options?.passage || ""
        const selected = new Set(
          (answers.selected || "").split("|").filter(Boolean),
        )
        const tokens =
          mode === "letter"
            ? passage.split("").map((t: string, i: number) => ({
                key: `L:${i}:${t}`,
                text: t,
              }))
            : passage.split(/(\s+)/).map((t: string, i: number) => ({
                key: `W:${i}:${t}`,
                text: t,
              }))
        return (
          <>
            {/* A count, not a "choose n" instruction: how many to pick is part of what is being
                assessed, so it is never given away - but a child who has tapped three words
                should be able to see that they have. */}
            <span
              className={`${styles.engDiagCounter} ${
                selected.size ? styles.engDiagCounterOn : ""
              }`}
            >
              {selected.size} {QCOPY.SELECT_COUNT_SUFFIX}
            </span>
            <div className={`${styles.engDiagPassage} ${styles.engDiagWordRow}`}>
              {tokens.map((token: { key: string; text: string }) => {
                if (!token.text.trim()) {
                  return <span key={token.key}>{token.text}</span>
                }
                const active = selected.has(token.key)
                return (
                  <button
                    key={token.key}
                    type="button"
                    aria-pressed={active}
                    className={`${styles.engDiagSelectToken} ${
                      mode === "letter" ? styles.engDiagMark : ""
                    } ${active ? styles.engDiagSelectTokenActive : ""}`}
                    onClick={() => {
                      const next = new Set(selected)
                      if (next.has(token.key)) next.delete(token.key)
                      else next.add(token.key)
                      emit(
                        setAnswer(answers, "selected", Array.from(next).join("|")),
                      )
                    }}
                  >
                    {token.text}
                  </button>
                )
              })}
            </div>
            <p className={styles.engDiagHint}>{QCOPY.SELECT_HINT}</p>
          </>
        )
      }
      case "sentence_rebuild": {
        const words = [
          ...(question.options?.words || []),
          ...(question.options?.distractors || []),
        ]
        const order = (answers.order || "").split("|").filter(Boolean)
        const placedIds = new Set(order)
        const setOrder = (next: string[]) =>
          emit(setAnswer(answers, "order", next.join("|")))
        return (
          <>
            <p className={styles.engDiagHint}>{QCOPY.REBUILD_HINT}</p>
            <div className={styles.engDiagSection}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span className={styles.engDiagSectionLabel}>{QCOPY.REBUILD_LABEL}</span>
                {/* Taking seven words back out one at a time is how a child gives up on a
                    question they could do. */}
                {order.length ? (
                  <button
                    type="button"
                    className={styles.engDiagBankItem}
                    style={{ fontSize: 18 }}
                    onClick={() => setOrder([])}
                  >
                    {QCOPY.REBUILD_RESET}
                  </button>
                ) : null}
              </div>
              {/* The bank words were already draggable, but nothing accepted a drop - this row
                  had no onDragOver - so every drag ended with the word back where it started
                  and the question looked broken. Tapping always worked, which is why it went
                  unnoticed.

                  No numbered empty slots, unlike the mockup: how many words the correct
                  sentence has is stripped out of what the student receives, and the bank can
                  hold distractors, so one slot per bank word would say that every word is
                  used. */}
              <div
                className={styles.engDiagRebuildZone}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault()
                  const wordId =
                    e.dataTransfer.getData("text/plain") || selectedBankId
                  if (!wordId || placedIds.has(wordId)) return
                  setOrder([...order, wordId])
                  setSelectedBankId(null)
                }}
              >
                {order.map(id => {
                  const word = words.find((w: any) => w.id === id)
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`${styles.engDiagWord} ${styles.engDiagWordActive}`}
                      aria-label={`Take ${word?.text || id} out of the sentence`}
                      onClick={() => setOrder(order.filter(x => x !== id))}
                    >
                      {word?.text || id}
                    </button>
                  )
                })}
                {!order.length ? (
                  <span className={styles.engDiagHint}>{QCOPY.REBUILD_EMPTY}</span>
                ) : null}
              </div>
            </div>
            <div className={styles.engDiagSection}>
              <span className={styles.engDiagSectionLabel}>{QCOPY.WORD_BANK}</span>
              <div className={styles.engDiagPanel}>
                {words.map((word: any) =>
                  placedIds.has(word.id) ? (
                    // A placeholder the same size as the word, so taking one out of the bank
                    // does not reflow it and move the word the child was about to tap next.
                    <span
                      key={word.id}
                      className={styles.engDiagChipGhost}
                      aria-hidden="true"
                    >
                      {word.text}
                    </span>
                  ) : (
                    <button
                      key={word.id}
                      type="button"
                      className={styles.engDiagWord}
                      draggable
                      onDragStart={onDragStart(word.id)}
                      onClick={() => setOrder([...order, word.id])}
                    >
                      {word.text}
                    </button>
                  ),
                )}
              </div>
            </div>
          </>
        )
      }
      case "reading_comp": {
        const subQuestions = question.options?.subQuestions || []
        return (
          <>
            {/* Same renderer as the question content: a passage is authored in the same
                editor, so a clip or a gap placed in one would otherwise render as nothing. */}
            <EngDiagRichContent
              className={`${styles.engDiagPassage} ${styles.engDiagProse}`}
              html={question.options?.passage || ""}
            />
            {/* Two kinds of sub-question, each answered under its own id. Comprehension is
                authored as multiple choice now, but questions written before that switch are
                still being sat, and they are marked on typed text. A sub-question written
                before the kind was recorded at all has no `type` - that means typed, never
                mcq, which is the same reading the contract and the marker take. */}
            <div className={styles.engDiagRows}>
              {subQuestions.map((sq: any, index: number) => (
                <div key={sq.id} className={styles.engDiagField}>
                  <div className="d-flex align-items-start gap-3">
                    <span className={styles.engDiagRowIndex} aria-hidden="true">
                      {index + 1}
                    </span>
                    {/* A label only where there is an input to label. A multiple-choice
                        sub-question is a radio group, named by aria-label below, and a `for`
                        pointing at nothing is worse than no label at all. */}
                    {sq.type === "mcq" ? (
                      <span className={styles.engDiagRowPrompt}>{sq.prompt}</span>
                    ) : (
                      <label className={styles.engDiagRowPrompt} htmlFor={`ed-${sq.id}`}>
                        {sq.prompt}
                      </label>
                    )}
                  </div>
                  {sq.type === "mcq" ? (
                    renderChoices(sq.choices || [], sq.id, sq.prompt)
                  ) : (
                    <input
                      id={`ed-${sq.id}`}
                      type="text"
                      autoComplete="off"
                      placeholder={QCOPY.TYPE_YOUR_ANSWER}
                      className={`${styles.engDiagInput} ${
                        (answers[sq.id] || "").trim() ? styles.engDiagInputFilled : ""
                      }`}
                      value={answers[sq.id] || ""}
                      onChange={e => emit(setAnswer(answers, sq.id, e.target.value))}
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        )
      }
      /*
       * The four types with nothing for the student to answer. Their responses are recorded
       * empty and marked by a human from the lesson report, which is why NOTHING_TO_ANSWER in
       * EngDiagAttemptPlayer lets the student submit one without touching anything.
       *
       * All four used to fall through to the default branch, so a child was shown
       * "Unsupported type: read_words" and the word list they were meant to read aloud was not
       * drawn at all.
       */
      case "read_words":
        return (
          <>
            {renderNoAnswerNote(
              "fas fa-comment-dots",
              QCOPY.READ_WORDS_TITLE,
              QCOPY.READ_WORDS_BODY,
            )}
            {renderWordList()}
          </>
        )
      case "phoneme_segmentation":
        return (
          <>
            {renderNoAnswerNote(
              "fas fa-comment-dots",
              QCOPY.PHONEMES_TITLE,
              QCOPY.PHONEMES_BODY,
            )}
            {renderWordList()}
          </>
        )
      case "flashcard":
        return (
          <>
            {renderNoAnswerNote(
              "fas fa-bolt",
              QCOPY.FLASHCARD_TITLE,
              QCOPY.FLASHCARD_BODY,
            )}
            {/* The authored showDelay / displayDuration timings are NOT played here: a timed
                presentation is a feature of its own, and this type is marked by the tutor
                either way. Showing the cards is what it needs to stop being a blank screen. */}
            <div className={styles.engDiagWordList} style={{ gridTemplateColumns: "1fr" }}>
              {(question.options?.cards || []).map((card: any) => (
                <div key={card.id} className={styles.engDiagWordListItem}>
                  {card.text}
                </div>
              ))}
            </div>
          </>
        )
      case "display_content":
        // Nothing but the content above, which is the whole point of the type.
        return renderNoAnswerNote(
          "fas fa-eye",
          QCOPY.DISPLAY_TITLE,
          QCOPY.DISPLAY_BODY,
        )
      default:
        return (
          <p className={styles.engDiagHint}>
            Unsupported type: {question.type}
          </p>
        )
    }
  }

  return (
    <div className={`${theme.engDiagTheme} ${styles.engDiagRoot}`}>
      {/* Authored in a rich-text editor, so both of these are HTML: 552 of the 560 questions
          have tags in the instruction, and as plain text it read "<p>Choose the sentence…</p>"
          verbatim.

          Routed through EngDiagRichContent, which turns the editor's placeholder spans into a
          play button and gives a cloze gap its width back. Without it a clip embedded in a
          question was silent here - a tutor could hear it in LCMS but not in the app. */}
      {question.instruction ? (
        <EngDiagRichContent
          className={styles.engDiagInstruction}
          html={question.instruction}
        />
      ) : null}
      <EngDiagRichContent
        className={styles.engDiagContent}
        html={question.content || ""}
      />
      {renderBody()}
    </div>
  )
}
